import { Button, notify } from '@hive/component';
import {
  SettingHeader,
  SettingRow,
} from '@hive/component/setting-components';
import { getUpgradeQuestionnaireLink } from '@hive/core/components/hooks/hive/use-subscription-notify';
import { useAsyncCallback } from '@hive/core/components/hooks/hive-async-hooks';
import { useMutation } from '@hive/core/components/hooks/use-mutation';
import {
  AuthService,
  WorkspaceSubscriptionService,
} from '@hive/core/modules/cloud';
import { WorkspacePermissionService } from '@hive/core/modules/permissions';
import { UrlService } from '@hive/core/modules/url';
import { WorkspaceService } from '@hive/core/modules/workspace';
import { UserFriendlyError } from '@hive/error';
import {
  createSelfhostCustomerPortalMutation,
  SubscriptionPlan,
  SubscriptionRecurring,
} from '@hive/graphql';
import { useI18n } from '@hive/i18n';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';

import { EnableCloudPanel } from '../preference/enable-cloud';
import { SelfHostTeamCard } from './self-host-team-card';
import { SelfHostTeamPlan } from './self-host-team-plan';
import * as styles from './styles.css';

export const WorkspaceSettingLicense = ({
  onCloseSetting,
}: {
  onCloseSetting: () => void;
}) => {
  const workspace = useService(WorkspaceService).workspace;

  const t = useI18n();

  if (workspace === null) {
    return null;
  }

  return (
    <FrameworkScope scope={workspace.scope}>
      <SettingHeader
        title={t['com.hive.settings.workspace.license']()}
        subtitle={t['com.hive.settings.workspace.license.description']()}
      />
      <SelfHostTeamPlan />
      {workspace.flavour === 'local' ? (
        <EnableCloudPanel onCloseSetting={onCloseSetting} />
      ) : (
        <>
          <SelfHostTeamCard />

          <PaymentMethodUpdater />
        </>
      )}
    </FrameworkScope>
  );
};

const PaymentMethodUpdater = () => {
  const workspace = useService(WorkspaceService).workspace;

  const permission = useService(WorkspacePermissionService).permission;
  const isTeam = useLiveData(permission.isTeam$);

  const { isMutating, trigger } = useMutation({
    mutation: createSelfhostCustomerPortalMutation,
  });
  const urlService = useService(UrlService);
  const t = useI18n();

  const update = useAsyncCallback(async () => {
    await trigger(
      {
        workspaceId: workspace.id,
      },
      {
        onSuccess: data => {
          urlService.openExternal(data.createSelfhostWorkspaceCustomerPortal);
        },
      }
    ).catch(e => {
      const userFriendlyError = UserFriendlyError.fromAny(e);
      notify.error(userFriendlyError);
    });
  }, [trigger, urlService, workspace.id]);

  if (!isTeam) {
    return null;
  }

  return (
    <SettingRow
      className={styles.paymentMethod}
      name={t['com.hive.payment.billing-setting.payment-method']()}
      desc={t[
        'com.hive.payment.billing-setting.payment-method.description'
      ]()}
    >
      <Button onClick={update} loading={isMutating} disabled={isMutating}>
        {t['com.hive.payment.billing-setting.payment-method.go']()}
      </Button>
    </SettingRow>
  );
};
